import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useReveal } from "@/hooks/use-reveal";
import { POSTS_BY_DATE, formatDate } from "@/content/blog";

const TITLE = "Field notes on running a business quietly";
const DESCRIPTION =
  "Practical essays for founders on daily goal tracking, monthly goal setting, running multiple businesses, the best apps for solo founders, why streak apps backfire, and restarting after a missed week.";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Founder Blog: Goal Tracking, Focus & Simple Systems" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://thefoundersapp.com/blog" },
      { property: "og:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://thefoundersapp.com/blog" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "The Founders App field notes",
          description: DESCRIPTION,
          url: "/blog",
          publisher: {
            "@type": "Organization",
            name: "Skylab Studio LLC",
            areaServed: "San Francisco Bay Area, California",
          },
          blogPost: POSTS_BY_DATE.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            description: p.description,
            datePublished: p.published,
            url: `/blog/${p.slug}`,
          })),
        }),
      },
    ],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  useReveal();

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="aurora" aria-hidden />
      <SiteHeader />

      <main id="main" className="relative mx-auto w-full max-w-5xl px-6 pt-16 pb-24 sm:px-10">
        <p className="reveal micro-label">Field notes</p>
        <h1 className="reveal etched mt-5 max-w-3xl text-[2.5rem] leading-[0.95] font-semibold sm:text-6xl [--d:80ms]">
          {TITLE}
        </h1>
        <p className="reveal mt-7 max-w-xl text-[0.95rem] leading-relaxed text-muted-foreground [--d:160ms]">
          {DESCRIPTION} Written for operators who would rather run the business than perform it.
        </p>

        <div className="hairline scan-rule mt-14" />

        <div className="mt-10 space-y-4">
          {POSTS_BY_DATE.map((post, i) => (
            <article
              key={post.slug}
              className="reveal soft-panel group p-7 sm:p-8"
              style={{ ["--d" as string]: `${120 + i * 80}ms` }}
            >
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="micro-label">{post.category}</span>
                <span className="text-xs text-muted-foreground/60">
                  <time dateTime={post.published}>{formatDate(post.published)}</time> ·{" "}
                  {post.readMinutes} min read
                </span>
              </div>

              <h2 className="mt-4 text-xl leading-snug font-medium sm:text-2xl">
                <Link
                  to="/blog/$slug"
                  params={{ slug: post.slug }}
                  className="transition-colors hover:text-foreground"
                >
                  {post.title}
                </Link>
              </h2>

              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                {post.excerpt}
              </p>

              <Link
                to="/blog/$slug"
                params={{ slug: post.slug }}
                className="micro-label mt-6 inline-flex items-center gap-2 transition-colors hover:text-foreground"
              >
                Read the note
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </article>
          ))}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
